import { useState } from 'react';
import { Button, Stack, CircularProgress, Box } from '@mui/material';
import SalesTable from './SalesTable';
import SalesScopeFilters, { SalesScope } from './SalesScopeFilters';
import PageLayout from '@shared/shell/Layout/PageLayout';
import { startOfWeek, endOfWeek } from 'date-fns';
import { Product } from '@domain/product/models/Product';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import { Paper } from '@shared/ui/common';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import { SalesChart } from '@features/index';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@shared/hooks/useToast';
import { container } from '@infrastructure/container';
import { Icon } from '@shared/icons';
import { SalesOnboardingOverlay } from '@features/tutorial';

const SHOW_NUMBER_OF_WEEKS = 12;
const SHOW_NUMBER_OF_FORECAST_WEEKS = 26;

const Sales = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [scope, setScope] = useState<SalesScope>({ marketplaces: [], countries: [] });

  const selectedMarketplaceIdsArray = scope.marketplaces.length > 0 ? scope.marketplaces : undefined;
  const selectedCountryCodesArray = scope.countries.length > 0 ? scope.countries : undefined;

  const [dateRange] = useState(() => {
    const endDate = endOfWeek(new Date());
    const startDate = startOfWeek(endDate);
    endDate.setDate(endDate.getDate() + (7 * SHOW_NUMBER_OF_FORECAST_WEEKS));
    startDate.setDate(startDate.getDate() - (7 * (SHOW_NUMBER_OF_WEEKS - 1)));
    return { startDate, endDate };
  });

  const navigateToProduct = (product: Product) => {
    navigate(`/products/${product.id}?tab=sales-timetravel`);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const result = await container.salesRepository.exportSalesCSV(
        dateRange.startDate,
        dateRange.endDate,
        selectedMarketplaceIdsArray,
        selectedCountryCodesArray,
      );

      if (result.status === 'error') {
        showError(result.error?.message || 'Failed to export sales CSV');
        return;
      }

      const blob = result.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_export_${dateRange.startDate.toISOString().split('T')[0]}_${dateRange.endDate.toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      success('Sales data exported successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to export sales CSV');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageLayout title="Sales">
      <Box sx={{ width: '100%', gap: '20px', py: '16px', borderTop: 1, borderColor: 'divider' }}>
        <SalesScopeFilters value={scope} onChange={setScope} />
      </Box>

      <SalesOnboardingOverlay>
        <SalesChart
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          marketplaces={selectedMarketplaceIdsArray}
          countries={selectedCountryCodesArray}
        />
      </SalesOnboardingOverlay>

      <Paper sx={{ mt: 4 }}>
        <Stack
          position="sticky"
          sx={{ backgroundColor: 'background.paper' }}
          borderRadius={2}
          top={0}
          zIndex={99}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          p={2}
          borderBottom={1}
          borderColor="divider"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <ProductsFilter />
            <ActiveFilters />
          </Stack>

          <Button
            variant="text"
            startIcon={isExporting ? <CircularProgress size={16} /> : <Icon icon="Download01" />}
            size="small"
            onClick={handleExportCSV}
            disabled={isExporting}
          >
            Export CSV
          </Button>
        </Stack>

        <SalesTable
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onProductSelect={navigateToProduct}
          marketplaces={selectedMarketplaceIdsArray}
          countries={selectedCountryCodesArray}
        />
      </Paper>
    </PageLayout>
  );
};

export default Sales;
